import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { MapContainer, TileLayer, Marker, Polygon, Tooltip, ZoomControl, useMapEvents, useMap, type MarkerProps } from 'react-leaflet'
import { Button, Input, useEinsatz, useEinsatzWechsel, useBestaetigung, useConnectionRetry, useDatenFehler, RequireEinsatz, FensterKnopf } from '@pilot-hub/ui'
import { api, userApi, vehicleApi } from '@pilot-hub/api-client'
import { FUNKTIONEN } from '@pilot-hub/config'
import 'leaflet/dist/leaflet.css'
import { FreigabeLinkButton } from '../components/FreigabeLinkDialog'
import { useFuehrungAktionen } from '../components/schnellAktionen'
import { KartenBuehne, vollbildVerlassen, type KartenPanelDef } from '../components/KartenPanel'
import L from 'leaflet'
import { MessLayer, MessPanelInhalt, messenAktiv, useMessen, geodaesie, GRUNDKARTEN, GEOZONEN, DIPUL_OWS, WmsGeozon, KartenDruck, legendeEintraege, quellenText, type GrundkarteWahl, type DruckAuswahl, type DruckEbene } from '@pilot-hub/ui/components/karte/index'
import {
  AlertTriangle, Check, Trash2, X, Plus, LocateFixed, Map as MapIcon, Layers, Crosshair, Ruler,
} from 'lucide-react'
import { SYMBOL_KATALOG, symbolDef, symbolSvg, symbolLabel, type Organisation } from '../components/taktischeSymbole'
import { SymbolInfoKarte, SymbolTooltip, hatMaus, symbolIconHtml, type BesatzungsMitglied, type TaktikZeichen, truppsAuswerten, fahrzeugRufnamenJeNutzer, truppZeichen, truppAnzeige } from '../components/taktischeSymbolUi'
import { useDrohnenLive, DrohnenLayer, DrohnenPanel, FlugroutenLayer } from '../components/DrohnenLive'
import { PilotMarkierungenLayer, usePilotMarkierungen } from '../components/PilotMarkierungen'
import { AbdeckungLayer, AbdeckungHinweis, ABDECKUNG_STANDARD_HOEHE_M, abdeckungProzent, useAbdeckung } from '../components/Abdeckung'

import { FotoLayer, HotspotLayer, MedienStatusHinweis, useFotos, useHotspots, useMedienStatus, type FotoFilter } from '../components/Lagebild'

/** Icons je Inhalt wiederverwenden: ein neues Icon-Objekt lässt react-leaflet setIcon() aufrufen,
 *  Leaflet ersetzt dann das Element und bricht ein laufendes Ziehen ab (v. a. per Touch am iPad,
 *  da die Karte alle 2 s die Drohnen neu lädt und dabei neu rendert). */
const iconCache = new Map<string, L.DivIcon>()
function divIcon(opts: L.DivIconOptions): L.DivIcon {
  const key = JSON.stringify(opts)
  let i = iconCache.get(key)
  if (!i) { i = L.divIcon(opts); iconCache.set(key, i) }
  return i
}

/* ------------------------------------------------------------------ Typen */

interface EinsatzOption {
  id: number
  titel: string
  status: string
  typ?: string | null
  einsatzort_lat?: number | null
  einsatzort_lon?: number | null
  einsatzort_adresse?: string | null
  beginn_geplant?: string | null
}

interface Fahrzeug { id: number; rufzeichen?: string | null; fahrzeugtyp?: string | null; kennzeichen?: string | null; typ?: string | null }

interface KartenSymbol {
  id: string
  typ: string
  lat: number
  lng: number
  kennung: string
  /** Fahrzeugtyp o. Ä. – Zusatzinfo, z. B. "GW-Drohne" */
  bezeichnung?: string
  /** Funkrufname, unter dem Zeichen angezeigt */
  rufname?: string
  /** Farbe nach Organisation; leer = Standard des Zeichens */
  organisation?: Organisation
  /** Zugeteiltes Fahrzeug, aus dem das Zeichen gesetzt wurde (für die Besatzung) */
  fahrzeugId?: number
  /** Trupp aus dem Kräfte-Management, aus dem das Zeichen gesetzt wurde */
  truppKey?: string
}

/** Besatzung je Fahrzeug aus dem Kräfte Management */
type BesatzungRoh = Record<number, { nutzerId: number; funktion?: string }[]>

/** Zugeteilte Fahrzeuge und deren Besatzung aus den Kräfte-Management-Daten eines Einsatzes */
function kraefteAuswerten(d: any): { fahrzeugIds: number[]; besatzung: BesatzungRoh } {
  const ids = new Set<number>()
  const besatzung: BesatzungRoh = {}
  for (const b of d?.bloecke ?? []) {
    if (b?.fahrzeugId == null) continue
    const id = Number(b.fahrzeugId)
    ids.add(id)
    besatzung[id] = (b.besatzung ?? []).filter((r: any) => r?.nutzerId != null)
      .map((r: any) => ({ nutzerId: Number(r.nutzerId), funktion: r.funktion || undefined }))
  }
  return { fahrzeugIds: [...ids], besatzung }
}

interface Suchgebiet {
  id: string
  kennung: string
  punkte: [number, number][]
}

interface LayerSicht {
  /** Hintergrundkarte: Straßenkarte, Luftbild oder topografisch mit Höhenlinien */
  grundkarte: GrundkarteWahl
  einsatzort: boolean
  symbole: boolean
  suchgebiete: boolean
  /** Rufnamen unter den Zeichen anzeigen */
  rufnamen: boolean
  /** Bisherige Flugroute der Drohnen im aktuellen Flug */
  flugrouten: boolean
  /** Markierungen, die der Pilot in DJI Pilot 2 zeichnet (fehlt in älteren Karten = an) */
  pilotmarker?: boolean
  /** Abdeckungsnachweis der Suchgebiete (Standard: aus) und größte Höhe über Grund, bis zu der ein Flug als abgedeckt zählt */
  abdeckung?: boolean
  abdeckungHoehe?: number
  /** Foto-Lagebild (Fotos aus DJI Pilot 2) und Hotspots aus IR-Fotos (Standard: aus) */
  fotos?: boolean
  hotspots?: boolean
  /** Master-Schalter für die DIPUL-Geozonen (§21h LuftVO) */
  luftraum: boolean
  /** Live-Drohnen auf der Karte (nur beim Drucken wählbar; fehlt = an) */
  drohnen?: boolean
  /** Einzelne Geozonen-Layer */
  geozonen: Record<string, boolean>
}

interface KarteData {
  layer?: LayerSicht
  symbole?: KartenSymbol[]
  suchgebiete?: Suchgebiet[]
}

const LEERE_LAYER: LayerSicht = {
  grundkarte: 'karte', einsatzort: true, symbole: true, suchgebiete: true, rufnamen: true, flugrouten: true, luftraum: false,
  geozonen: {},
}

interface GeozonTreffer {
  zone: string
  farbe: string
  name: string
  kategorie: string
  rechtsgrundlage: string
  untergrenze: string
  obergrenze: string
  gueltigkeit: string
}

let zaehler = 0
const neueId = (p: string) => `${p}-${Date.now().toString(36)}-${++zaehler}`

/* ------------------------------------------------------- Karte: Maushilfen */

/**
 * Zentriert die Karte auf den Einsatzort – auch wenn der Einsatz später
 * gewechselt wird (MapContainer wertet `center` nur beim ersten Rendern aus).
 * Zusätzlich invalidateSize, damit die Kacheln nach dem Aufbau korrekt sitzen.
 */
/** Kompakter Ein/Aus-Schalter für Kartenebenen (Toggle-Chip, mind. 44 px hoch) */
function EbenenChip({ an, onChange, children, farbe, title }: { an: boolean; onChange: (v: boolean) => void; children: ReactNode; farbe?: string; title?: string }) {
  return (
    <button type="button" role="switch" aria-checked={an} title={title} onClick={() => onChange(!an)}
      className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${an ? 'border-primary-400 bg-primary-500/25 text-dark-text' : 'border-dark-border bg-dark-bg-tertiary/50 text-dark-text-subtle hover:text-dark-text'}`}>
      {farbe && <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: farbe }} />}
      {children}
    </button>
  )
}

/** Koordinaten (Dezimalgrad, z. B. „52.5200, 13.4050“) eingeben und die Karte dorthin zoomen */
function PositionZoomDialog({ onClose, onZoom }: { onClose: () => void; onZoom: (lat: number, lon: number) => void }) {
  const [text, setText] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const absenden = () => {
    const z = text.replace(/,/g, ' ').trim().split(/\s+/).map(Number)
    if (z.length !== 2 || z.some(n => !Number.isFinite(n)) || Math.abs(z[0]) > 90 || Math.abs(z[1]) > 180) {
      // deutsche Schreibweise „52,52 13,405“ wird oben durch die Kommatrennung nicht erkannt -> klare Meldung
      setFehler('Bitte Breite und Länge als Dezimalgrad mit Punkt eingeben, z. B. 52.5200 13.4050')
      return
    }
    onZoom(z[0], z[1])
  }
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-end justify-center bg-black/60 sm:items-center" onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <div role="dialog" aria-modal="true" aria-label="Nach Position zoomen" data-testid="position-dialog"
        className="w-full rounded-t-2xl border border-dark-border bg-dark-bg-secondary p-4 shadow-2xl sm:max-w-sm sm:rounded-2xl">
        <h2 className="mb-3 text-lg font-semibold text-dark-text">Nach Position zoomen</h2>
        <input autoFocus value={text} onChange={e => { setText(e.target.value); setFehler(null) }}
          onKeyDown={e => { if (e.key === 'Enter') absenden(); if (e.key === 'Escape') onClose() }}
          placeholder="52.5200 13.4050" inputMode="decimal" aria-label="Breite und Länge"
          className="h-11 w-full rounded-lg border border-dark-border bg-dark-bg-tertiary px-3 text-dark-text" />
        {fehler && <p className="mt-2 text-sm text-red-300" role="alert">{fehler}</p>}
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="secondary" className="min-h-[44px]" onClick={onClose}>Abbrechen</Button>
          <Button className="min-h-[44px]" onClick={absenden}>Zoomen</Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function ZentriereAuf({ ziel, zoom }: { ziel: [number, number] | null; zoom: number }) {
  const map = useMap()
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 150)
    return () => clearTimeout(t)
  }, [map])
  useEffect(() => {
    if (ziel) map.setView(ziel, zoom, { animate: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, ziel?.[0], ziel?.[1], zoom])
  return null
}

function KlickHandler({ aktiv, onKlick }: { aktiv: boolean; onKlick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click: e => { if (aktiv && !messenAktiv(e.target as L.Map)) onKlick(e.latlng.lat, e.latlng.lng) },   // beim Messen setzt nur das Messwerkzeug Punkte
  })
  return null
}

/** Merkt sich die Karteninstanz für Koordinaten-Umrechnung beim Drop. */
function KartenRef({ mapRef }: { mapRef: React.MutableRefObject<L.Map | null> }) {
  const map = useMapEvents({})
  useEffect(() => { mapRef.current = map }, [map, mapRef])
  return null
}

/**
 * Marker, dessen Position nur bei echter Koordinatenänderung neu gesetzt wird.
 * react-leaflet ruft bei jeder neuen position-Referenz setLatLng auf – durch das
 * Drohnen-Polling (alle 2 s) sprang ein gerade gezogenes Symbol sonst zurück.
 */
function StabilerMarker({ lat, lng, ...rest }: Omit<MarkerProps, 'position'> & { lat: number; lng: number }) {
  const position = useMemo<[number, number]>(() => [lat, lng], [lat, lng])
  return <Marker position={position} {...rest} />
}

/* ==================================================================== Seite */

export function TaktischeKartePage() {
  // Globale Einsatzwahl (Provider in App.tsx). Aus dem Dashboard (Karte „Aktive Flüge“): ?einsatz=<id>&drohne=<Seriennummer>
  // – den Einsatz übernimmt der Provider, die Drohne wird hier zentriert.
  const [suchParams, setSuchParams] = useSearchParams()
  const { einsatzIdStr: einsatzId, einsatz: gewaehlt } = useEinsatz()
  const [fahrzeuge, setFahrzeuge] = useState<Fahrzeug[]>([])
  const [zugeordneteFahrzeuge, setZugeordneteFahrzeuge] = useState<number[]>([])
  const [besatzungRoh, setBesatzungRoh] = useState<BesatzungRoh>({})
  /** Rohdaten des Kräfte-Managements (Trupps live) */
  const [kmRoh, setKmRoh] = useState<any>(null)
  const [nutzerNamen, setNutzerNamen] = useState<Record<number, string>>({})

  const [symbole, setSymbole] = useState<KartenSymbol[]>([])
  const [suchgebiete, setSuchgebiete] = useState<Suchgebiet[]>([])
  const [layerEcht, setLayer] = useState<LayerSicht>({ ...LEERE_LAYER })
  /** Nur während „Drucken / PDF“: gewählte Ebenen, werden nie gespeichert */
  const [druckLayer, setDruckLayer] = useState<LayerSicht | null>(null)
  const layer = druckLayer ?? layerEcht

  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(false)
  const [speicherStatus, setSpeicherStatus] = useState<'leer' | 'speichert' | 'gespeichert' | 'fehler'>('leer')
  const [ladeFehler, setLadeFehler] = useState<string | null>(null)
  const [ladeNeu, setLadeNeu] = useState(0)
  const datenFehler = useDatenFehler()
  const [kraefteFehler, setKraefteFehler] = useState(false)
  const [geozonFehler, setGeozonFehler] = useState(false)

  /** Aktiver Setz-Modus: null = aus, 'suchgebiet' = Eckpunkte setzen, sonst Symboltyp */
  const [modus, setModus] = useState<string | null>(null)
  /** Bei einem Fahrzeug aus der Leiste: welches (mehrere Fahrzeuge können denselben Symboltyp haben) */
  const [modusFahrzeug, setModusFahrzeug] = useState<number | null>(null)
  const [modusTrupp, setModusTrupp] = useState<string | null>(null)
  const [geozonTreffer, setGeozonTreffer] = useState<GeozonTreffer[] | null>(null)
  const [geozonOrt, setGeozonOrt] = useState<[number, number] | null>(null)
  const [geozonLaedt, setGeozonLaedt] = useState(false)
  const [bearbeitet, setBearbeitet] = useState<string | null>(null)
  const [dragSymbol, setDragSymbol] = useState<{ typ: string; kennung: string; bezeichnung?: string; rufname?: string; x: number; y: number } | null>(null)

  const mapRef = useRef<L.Map | null>(null)
  // Live-Telemetrie der über DJI Pilot 2 verbundenen Drohnen
  const { drohnen: liveDrohnen, amBoden: liveAmBoden, routen: flugrouten, neuLaden: liveNeuLaden } = useDrohnenLive()
  const zentrierDrohne = suchParams.get('drohne')
  useEffect(() => {
    if (!zentrierDrohne) return
    const d = liveDrohnen.find(x => x.sn === zentrierDrohne)
    const map = mapRef.current
    if (!d?.daten.lat || !d?.daten.lon || !map) return
    // kurz warten, damit das automatische Zentrieren auf den Einsatzort die Drohne nicht wieder verdrängt
    const t = setTimeout(() => {
      map.flyTo([d.daten.lat!, d.daten.lon!], Math.max(map.getZoom(), 17), { duration: 0.8 })
      setSuchParams({}, { replace: true })
    }, 600)
    return () => clearTimeout(t)
  }, [zentrierDrohne, liveDrohnen, mapRef, setSuchParams, einsatzId])
  const pilotMarkierungen = usePilotMarkierungen(einsatzId)
  const abdeckung = useAbdeckung(einsatzId, layer.abdeckungHoehe ?? ABDECKUNG_STANDARD_HOEHE_M, layer.abdeckung === true, suchgebiete.length > 0)
  const [fotoFilter, setFotoFilter] = useState<FotoFilter>('alle')
  /** Messwerkzeuge (Strecke, Fläche, Kreis, Peilung, Koordinate; nur lokal und temporär) */
  const messen = useMessen()
  const fotos = useFotos(einsatzId, layer.fotos === true)
  const hotspots = useHotspots(einsatzId, layer.hotspots === true)
  const medienStatus = useMedienStatus(layer.fotos === true || layer.hotspots === true)
  const hydriert = useRef(false)
  /** Einsatz-ID, zu der die aktuell im Zustand liegende Karte gehört (beim Laden gemerkt) */
  const geladeneId = useRef('')
  /** Änderungen seit dem letzten erfolgreichen Speichern */
  const ungespeichert = useRef(false)
  const speicherVersion = useRef(0)
  /** Zeitpunkt des letzten Ziehens – verhindert Löschen direkt nach dem Verschieben */
  const letztesZiehen = useRef(0)
  const downRef = useRef<{ typ: string; kennung: string; bezeichnung?: string; rufname?: string; fahrzeugId?: number; truppKey?: string; x: number; y: number; moved: boolean } | null>(null)
  const dragRef = useRef<{ typ: string; kennung: string; bezeichnung?: string; rufname?: string; x: number; y: number } | null>(null)

  const einsatz = (gewaehlt?.raw ?? null) as EinsatzOption | null
  const einsatzOrt = useMemo<[number, number] | null>(() => {
    const la = einsatz?.einsatzort_lat, lo = einsatz?.einsatzort_lon
    return la != null && lo != null ? [la, lo] : null
  }, [einsatz])

  /* ------------------------------------------------------------- Laden */

  const laden = useCallback(async () => {
    setLaedt(true); setFehler(null)
    try {
      const [f, n] = await Promise.all([
        vehicleApi.list({ size: 100 }),
        userApi.list({ size: 500 }),
      ])
      setFahrzeuge(((f.data as any)?.items ?? []) as Fahrzeug[])
      const namen: Record<number, string> = {}
      for (const u of ((n.data as any)?.items ?? []) as any[]) {
        namen[u.id] = [u.vorname, u.nachname].filter(Boolean).join(' ') || u.benutzername
      }
      setNutzerNamen(namen)
      // Einsatzliste und Vorauswahl (laufend vor geplant) liefert der EinsatzProvider
    } catch {
      setFehler('Fahrzeuge und Nutzer konnten nicht geladen werden.')
    } finally {
      setLaedt(false)
    }
  }, [])
  useEffect(() => { laden() }, [laden])
  useConnectionRetry(() => { void laden(); if (ladeFehler) setLadeNeu(n => n + 1) }) // Karte nur neu laden, wenn sie nicht geladen werden konnte (sonst gingen Änderungen verloren)

  /* ------------------------------- Karte + zugeteilte Fahrzeuge je Einsatz */

  useEffect(() => {
    if (!einsatzId) {
      setSymbole([]); setSuchgebiete([]); setLayer({ ...LEERE_LAYER })
      setZugeordneteFahrzeuge([]); setBesatzungRoh({}); setKmRoh(null); hydriert.current = false; setLadeFehler(null)
      setSpeicherStatus('leer')
      return
    }
    let cancelled = false
    hydriert.current = false; setLadeFehler(null); setLaedt(true)
    ;(async () => {
      let karte: KarteData | null = null
      for (let versuch = 0; versuch < 3; versuch++) {
        try {
          const [k, km] = await Promise.all([
            api.get(`/taktische-karte/${einsatzId}`),
            api.get(`/kraefte-management/${einsatzId}`),
          ])
          if (cancelled) return
          karte = ((k.data as any)?.data ?? {}) as KarteData
          const km_ = kraefteAuswerten((km.data as any)?.data)
          setKmRoh((km.data as any)?.data ?? null)
          setZugeordneteFahrzeuge(km_.fahrzeugIds)
          setBesatzungRoh(km_.besatzung)
          break
        } catch {
          if (cancelled) return
          if (versuch === 2) setLadeFehler('Kartendaten konnten nicht geladen werden.')
          else await new Promise(r => setTimeout(r, 600))
        }
      }
      if (cancelled) return
      setSymbole(Array.isArray(karte?.symbole) ? (karte!.symbole as KartenSymbol[]) : [])
      setSuchgebiete(Array.isArray(karte?.suchgebiete) ? (karte!.suchgebiete as Suchgebiet[]) : [])
      // Ältere Karten speichern noch `hoehenlinien: boolean` statt der Grundkarte
      const { hoehenlinien, ...gespeichert } = (karte?.layer ?? {}) as Partial<LayerSicht> & { hoehenlinien?: boolean }
      setLayer({
        ...LEERE_LAYER, ...gespeichert,
        grundkarte: gespeichert.grundkarte ?? (hoehenlinien ? 'topo' : 'karte'),
        geozonen: { ...(gespeichert.geozonen ?? {}) },
      })
      hydriert.current = true
      geladeneId.current = einsatzId   // Speichern immer mit der beim Laden gemerkten ID
      ungespeichert.current = false
      setSpeicherStatus(karte && (karte.symbole?.length || karte.suchgebiete?.length) ? 'gespeichert' : 'leer')
      setLaedt(false)
    })()
    return () => { cancelled = true }
  }, [einsatzId, ladeNeu])

  /* --------------------------------------------------------- Autosave */

  useEffect(() => {
    if (!einsatzId || !hydriert.current || ladeFehler) return
    const ziel = einsatzId, version = ++speicherVersion.current
    ungespeichert.current = true
    const t = setTimeout(async () => {
      setSpeicherStatus('speichert')
      try {
        await api.put(`/taktische-karte/${ziel}`, { data: { layer: layerEcht, symbole, suchgebiete } })
        if (speicherVersion.current === version) ungespeichert.current = false
        setSpeicherStatus('gespeichert')
      } catch { setSpeicherStatus('fehler') }
    }, 900)
    return () => clearTimeout(t)
  }, [einsatzId, layerEcht, symbole, suchgebiete, ladeFehler])

  // Einsatzwechsel: ausstehende Änderungen vorher speichern (mit der beim Laden gemerkten ID); schlägt das fehl, fragen/abbrechen
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung()
  useEinsatzWechsel(async () => {
    if (!hydriert.current || ladeFehler || !ungespeichert.current || !geladeneId.current) return
    const ziel = geladeneId.current, version = speicherVersion.current
    setSpeicherStatus('speichert')
    try {
      await api.put(`/taktische-karte/${ziel}`, { data: { layer: layerEcht, symbole, suchgebiete } })
      if (speicherVersion.current === version) ungespeichert.current = false
      setSpeicherStatus('gespeichert')
    } catch {
      setSpeicherStatus('fehler')
      await vollbildVerlassen() // im Vollbild wäre der Dialog (außerhalb der Bühne) unsichtbar
      return bestaetigen({ titel: 'Karte nicht gespeichert', text: 'Die Karte konnte nicht gespeichert werden. Trotzdem den Einsatz wechseln und die letzten Änderungen verwerfen?', bestaetigenText: 'Trotzdem wechseln', abbrechenText: 'Hier bleiben' })
    }
  })

  // Besatzung aktuell halten: Zuteilungen im Kräfte Management ändern sich während des Einsatzes
  useEffect(() => {
    if (!einsatzId) return
    setKraefteFehler(false)
    const t = setInterval(async () => {
      try {
        const kmd = ((await api.get(`/kraefte-management/${einsatzId}`)).data as any)?.data
        const km = kraefteAuswerten(kmd)
        setKmRoh(kmd ?? null)
        setZugeordneteFahrzeuge(km.fahrzeugIds)
        setBesatzungRoh(km.besatzung)
        setKraefteFehler(false)
      } catch { setKraefteFehler(true) } // nächster Versuch beim nächsten Intervall; Verbindung meldet der Interceptor
    }, 10000)
    return () => clearInterval(t)
  }, [einsatzId])

  const trupps = useMemo(() => truppsAuswerten(kmRoh), [kmRoh])
  const truppRufnamen = useMemo(() => fahrzeugRufnamenJeNutzer(kmRoh, id => {
    const f = fahrzeuge.find(x => x.id === id)
    return f ? (f.rufzeichen || f.kennzeichen || undefined) : undefined
  }), [kmRoh, fahrzeuge])
  /** Gesetzte Trupp-Zeichen: Name/Typ immer aus dem aktuellen Kräfte-Management */
  const effektiv = useCallback((s: KartenSymbol): KartenSymbol => truppZeichen(s, trupps), [trupps])

  /** Besatzung eines Kartenzeichens: über die gemerkte Fahrzeug-ID, bei älteren Zeichen über den Rufnamen */
  const besatzungFuer = useCallback((s: KartenSymbol): BesatzungsMitglied[] => {
    let id = s.fahrzeugId
    if (id == null && s.rufname) {
      id = zugeordneteFahrzeuge.find(fid => {
        const f = fahrzeuge.find(x => x.id === fid)
        return f && (f.rufzeichen || f.kennzeichen) === s.rufname
      })
    }
    if (id == null) return []
    return (besatzungRoh[id] ?? []).map(r => ({
      name: nutzerNamen[r.nutzerId] ?? `Nutzer #${r.nutzerId}`,
      funktion: r.funktion ? (FUNKTIONEN.find(f => f.value === r.funktion)?.label ?? r.funktion) : undefined,
    }))
  }, [besatzungRoh, nutzerNamen, zugeordneteFahrzeuge, fahrzeuge])

  /* --------------------------------------------- Platzieren (Klick/Drop) */

  const platziere = useCallback((typ: string, lat: number, lng: number, kennung?: string, bezeichnung?: string, rufname?: string, fahrzeugId?: number, truppKey?: string) => {
    const std = kennung ?? symbolDef(typ)?.standardKennung ?? ''
    setSymbole(prev => [...prev, {
      id: neueId('sym'), typ, lat, lng, kennung: std, bezeichnung,
      ...(rufname ? { rufname } : {}),
      ...(fahrzeugId != null ? { fahrzeugId } : {}),
      ...(truppKey ? { truppKey } : {}),
    }])
    setModus(null)
  }, [])

  /** Palette-Einträge: taktische Zeichen + die dem Einsatz zugeteilten Fahrzeuge. */
  const palette = useMemo(() => {
    // Mapping: DB fahrzeugtyp -> unser Symbol-Typ
    const fahrzeugTypZuSymbol: Record<string, string> = {
      'ELW': 'elw1',
      'ELW 1': 'elw1',
      'ELW 2': 'elw2',
      'Löschfahrzeug': 'loeschfahrzeug',
      'LF': 'loeschfahrzeug',
      'RTW': 'rtw',
      'Rettungswagen': 'rtw',
      'KTW': 'ktw',
      'Krankentransportwagen': 'ktw',
      'NEF': 'nef',
      'Notarzteinsatzfahrzeug': 'nef',
      'GW-Drohne': 'gw_drohne',
      'GW-Drone': 'gw_drohne',
      'GW-Hundestaffel': 'gw_hundestaffel',
      'GW-Hund': 'gw_hundestaffel',
      'Einsatzfahrzeug': 'einsatzfahrzeug',
      'Abrollbehälter': 'abrollbehaelter',
      'Anhänger': 'anhaenger',
      'Hubschrauber': 'hubschrauber',
      'Wasserfahrzeug': 'wasserfahrzeug',
    }

    const kurzKennung = (f: Fahrzeug, symTyp: string): string => {
      const typText = f.fahrzeugtyp || f.typ || ''
      if (typText && typText.length <= 10) return typText
      return symbolDef(symTyp)?.standardKennung ?? ''
    }

    const holeSymbolTyp = (f: Fahrzeug): string => {
      const keys = [f.fahrzeugtyp, f.typ].filter((v): v is string => Boolean(v))
      for (const key of keys) {
        if (fahrzeugTypZuSymbol[key]) return fahrzeugTypZuSymbol[key]
        // Teilstring-Match (z. B. "GW-Drohne" in "GW-Drohne 56-79-1")
        for (const [dbTyp, symTyp] of Object.entries(fahrzeugTypZuSymbol)) {
          if (key.includes(dbTyp)) return symTyp
        }
      }
      return 'fahrzeug'  // Fallback
    }

    const fzg = zugeordneteFahrzeuge
      .map((id) => fahrzeuge.find(x => x.id === id))
      .filter((v): v is Fahrzeug => Boolean(v))
      .map((f) => {
        const symTyp = holeSymbolTyp(f)
        return {
          typ: symTyp,
          // Im Zeichen steht der Fahrzeugtyp (kurz), das Rufzeichen steht als Rufname darunter.
          label: [f.fahrzeugtyp || f.typ || 'Fahrzeug', f.rufzeichen || f.kennzeichen || `#${f.id}`]
            .filter(Boolean).join(' · '),
          kennung: kurzKennung(f, symTyp),
          rufname: f.rufzeichen || f.kennzeichen || '',
          bezeichnung: f.fahrzeugtyp || f.typ || 'Fahrzeug',
          fahrzeugId: f.id as number | undefined,
          truppKey: undefined as string | undefined,
          group: 'Zugeteilte Fahrzeuge',
        }
      })

    const trp = trupps.map(t => ({
      typ: symbolDef(t.typ) ? t.typ : 'drohnentrupp',
      label: `${t.name} · ${symbolLabel(t.typ)}`,
      kennung: t.name,
      rufname: undefined as string | undefined,
      bezeichnung: symbolLabel(t.typ) as string | undefined,
      fahrzeugId: undefined as number | undefined,
      truppKey: t.key as string | undefined,
      group: 'Trupps',
    }))

    return [
      ...fzg,
      ...trp,
      ...SYMBOL_KATALOG.map(s => ({ typ: s.typ, label: s.label, kennung: s.standardKennung ?? '', bezeichnung: undefined as string | undefined, rufname: undefined as string | undefined, fahrzeugId: undefined as number | undefined, truppKey: undefined as string | undefined, group: s.group })),
    ]  }, [zugeordneteFahrzeuge, fahrzeuge, trupps])

  /* ------------------------------------------- Pointer-Drag aus der Leiste */

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = downRef.current
      if (!d) return
      if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 8) return
      d.moved = true
      const next = { typ: d.typ, kennung: d.kennung, bezeichnung: d.bezeichnung, rufname: d.rufname, x: e.clientX, y: e.clientY }
      dragRef.current = next
      setDragSymbol(next)
    }
    const onUp = (e: PointerEvent) => {
      const d = downRef.current
      const cur = dragRef.current
      downRef.current = null; dragRef.current = null; setDragSymbol(null)
      if (!d?.moved || !cur) return
      const map = mapRef.current
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null
      const karte = el?.closest('.leaflet-container') as HTMLElement | null
      if (!map || !karte) return
      const r = karte.getBoundingClientRect()
      const ll = map.containerPointToLatLng(L.point(e.clientX - r.left, e.clientY - r.top))
      platziere(cur.typ, ll.lat, ll.lng, cur.kennung, cur.bezeichnung, cur.rufname, d.fahrzeugId, d.truppKey)
    }
    window.addEventListener('pointermove', onMove, { passive: false })
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [platziere])

  /* ------------------------------------------------------------ Aktionen */

  const verschiebeSymbol = (id: string, lat: number, lng: number) =>
    setSymbole(p => p.map(s => (s.id === id ? { ...s, lat, lng } : s)))
  const loescheSymbol = (id: string) => {
    setSymbole(p => p.filter(s => s.id !== id)); setBearbeitet(null)
  }
  const aendereSymbol = (id: string, patch: Partial<TaktikZeichen>) =>
    setSymbole(p => p.map(s => (s.id === id ? { ...s, ...patch } : s)))

  /** Geozonen im Umfeld des Klickpunkts über WFS abfragen (±150 m). */
  const frageGeozonenAb = useCallback(async (lat: number, lng: number) => {
    const aktive = GEOZONEN.filter(g => layer.luftraum && layer.geozonen[g.key])
    setGeozonOrt([lat, lng])
    if (aktive.length === 0) { setGeozonTreffer([]); return }
    setGeozonLaedt(true); setGeozonFehler(false)
    let fehlgeschlagen = false
    const d = 0.0015                                  // ca. 150 m
    const bbox = `${lng - d},${lat - d},${lng + d},${lat + d},EPSG:4326`   // lon,lat!
    const treffer: GeozonTreffer[] = []
    await Promise.all(aktive.map(async gz => {
      try {
        const r = await fetch(
          `${DIPUL_OWS}?service=WFS&version=2.0.0&request=GetFeature&typeName=dipul:${gz.layer}` +
          `&count=5&outputFormat=application/json&srsName=EPSG:4326&bbox=${bbox}`,
        )
        if (!r.ok) { fehlgeschlagen = true; return }
        const j = await r.json()
        for (const f of j.features ?? []) {
          const p: any = f.properties ?? {}
          const ug = p.lower_limit_altitude != null
            ? `${p.lower_limit_altitude} ${p.lower_limit_unit ?? ''} ${p.lower_limit_alt_ref ?? ''}`.trim() : ''
          const og = p.upper_limit_altitude != null
            ? `${p.upper_limit_altitude} ${p.upper_limit_unit ?? ''} ${p.upper_limit_alt_ref ?? ''}`.trim() : ''
          treffer.push({
            zone: gz.label,
            farbe: gz.farbe,
            name: p.generated_name_DE || p.name || '—',
            kategorie: p.type_code_detail || p.type_code || '—',
            rechtsgrundlage: p.legal_ref ?? '—',
            untergrenze: ug, obergrenze: og,
            gueltigkeit: p.start_time && p.end_time
              ? `${new Date(p.start_time).toLocaleString('de-DE')} – ${new Date(p.end_time).toLocaleString('de-DE')}` : '',
          })
        }
      } catch { fehlgeschlagen = true } // Fremddienst (DIPUL): nicht im Verbindungsstatus, aber sichtbar im Panel
    }))
    setGeozonFehler(fehlgeschlagen)
    setGeozonTreffer(treffer)                          // Doppelte zusammenfassen
    setGeozonLaedt(false)
  }, [layer.luftraum, layer.geozonen])

  const klickAufKarte = (lat: number, lng: number) => {
    if (modus === 'suchgebiet') {
      setSuchgebiete(prev => {
        if (prev.length === 0) {
          return [{ id: neueId('sg'), kennung: `Suchgebiet ${prev.length + 1}`, punkte: [[lat, lng]] }]
        }
        const letztes = prev[prev.length - 1]
        return [...prev.slice(0, -1), { ...letztes, punkte: [...letztes.punkte, [lat, lng]] }]
      })
    } else if (modus) {
      const pe = palette.find(p => p.typ === modus && (p.fahrzeugId ?? null) === modusFahrzeug && (p.truppKey ?? null) === modusTrupp)
      platziere(modus, lat, lng, pe?.kennung || undefined, pe?.bezeichnung, pe?.rufname, pe?.fahrzeugId, pe?.truppKey)
    } else {
      void frageGeozonenAb(lat, lng)
    }
  }

  const verschiebeEckpunkt = (sgId: string, idx: number, lat: number, lng: number) =>
    setSuchgebiete(p => p.map(sg => sg.id !== sgId ? sg : {
      ...sg, punkte: sg.punkte.map((pt, i) => (i === idx ? [lat, lng] as [number, number] : pt)),
    }))
  const loescheEckpunkt = (sgId: string, idx: number) =>
    setSuchgebiete(p => p.flatMap(sg => {
      if (sg.id !== sgId) return [sg]
      const punkte = sg.punkte.filter((_, i) => i !== idx)
      return punkte.length >= 2 ? [{ ...sg, punkte }] : []
    }))
  const loescheGebiet = (sgId: string) => setSuchgebiete(p => p.filter(sg => sg.id !== sgId))
  const neuesGebiet = () => {
    // Bewusst ohne Startpunkt: alle Eckpunkte werden selbst gesetzt.
    // Noch leere Gebiete (Zeichnen ohne Klick auf die Karte beendet) werden dabei verworfen.
    setSuchgebiete(p => {
      const belegt = p.filter(sg => sg.punkte.length > 0)
      return [...belegt, { id: neueId('sg'), kennung: `Suchgebiet ${belegt.length + 1}`, punkte: [] }]
    })
    setModus('suchgebiet')
  }
  // Zeichnen beenden bzw. starten; beim Beenden leere Gebiete entfernen
  const suchgebietUmschalten = () => {
    if (modus === 'suchgebiet') {
      setModus(null)
      setSuchgebiete(p => p.filter(sg => sg.punkte.length > 0))
    } else {
      neuesGebiet()
    }
  }

  // Aktionen für Befehlspalette (Strg+K) und Schnellaktionen-Sheet (Tablet), solange die Karte offen ist
  const [positionOffen, setPositionOffen] = useState(false)
  useFuehrungAktionen(einsatzId ? [
    { id: 'karte-suchgebiet', label: modus === 'suchgebiet' ? 'Suchgebiet zeichnen beenden' : 'Suchgebiet zeichnen', group: 'Karte', keywords: 'gebiet fläche polygon suche zeichnen', icon: <Plus className="h-4 w-4" />, run: () => suchgebietUmschalten() },
    { id: 'karte-position', label: 'Nach Position zoomen', group: 'Karte', keywords: 'koordinate zoom springen position lat lon', icon: <LocateFixed className="h-4 w-4" />, run: () => setPositionOffen(true) },
  ] : [], [einsatzId, modus])

  const icon = (s: KartenSymbol, aktiv = false) => divIcon({
    html: symbolIconHtml(s, aktiv, layer.rufnamen !== false),
    className: 'taktik-symbol',
    iconSize: [52, 40],
    iconAnchor: [26, 20],
  })

  const mitte: [number, number] = einsatzOrt ?? [49.8728, 8.6512]

  /* ------------------------------------------------------------------ UI */

  // Höhenlinien bewusst NICHT hier – sie stehen als letzter Schalter unten.
  const layerSchalter: Array<{ key: 'einsatzort' | 'symbole' | 'rufnamen' | 'suchgebiete' | 'flugrouten' | 'pilotmarker'; label: string }> = [
    { key: 'einsatzort', label: 'Einsatzort (roter Punkt)' },
    { key: 'symbole', label: 'Taktische Symbole' },
    { key: 'rufnamen', label: 'Rufnamen unter den Zeichen' },
    { key: 'suchgebiete', label: 'Suchgebiete' },
    { key: 'flugrouten', label: 'Flugrouten (aktueller Flug)' },
    { key: 'pilotmarker', label: 'Markierungen aus DJI Pilot 2' },
  ]

  const grundkarteWahl = (
    <div className="mb-2 flex overflow-hidden rounded-xl border border-dark-border">
      {GRUNDKARTEN.map(g => (
        <button key={g.key} type="button" aria-pressed={layer.grundkarte === g.key}
          onClick={() => setLayer(v => ({ ...v, grundkarte: g.key }))}
          className={`min-h-[44px] flex-1 px-2 text-xs font-semibold transition-colors ${layer.grundkarte === g.key
            ? 'bg-primary-500 text-white'
            : 'text-dark-text-muted hover:bg-dark-bg-tertiary hover:text-dark-text'}`}>
          {g.label}
        </button>
      ))}
    </div>
  )

  /* ------------------------------------------------- Drucken / PDF */
  const druckEbenen: DruckEbene[] = [
    { key: 'einsatzort', label: 'Einsatzort (roter Punkt)', an: layerEcht.einsatzort !== false },
    { key: 'symbole', label: 'Taktische Symbole', an: layerEcht.symbole !== false },
    { key: 'rufnamen', label: 'Rufnamen unter den Zeichen', an: layerEcht.rufnamen !== false, unter: true },
    { key: 'suchgebiete', label: 'Suchgebiete', an: layerEcht.suchgebiete !== false },
    { key: 'flugrouten', label: 'Flugrouten (aktueller Flug)', an: layerEcht.flugrouten !== false },
    { key: 'pilotmarker', label: 'Markierungen aus DJI Pilot 2', an: layerEcht.pilotmarker !== false },
    { key: 'drohnen', label: 'Drohnen live', an: layerEcht.drohnen !== false },
    { key: 'abdeckung', label: 'Abdeckung (von der Kamera gesehen)', an: layerEcht.abdeckung === true },
    { key: 'fotos', label: 'Lagebild (Fotos)', an: layerEcht.fotos === true },
    { key: 'hotspots', label: 'Hotspots (IR-Fotos)', an: layerEcht.hotspots === true },
    { key: 'luftraum', label: 'Luftraum / Schutzgebiete (DIPUL)', an: layerEcht.luftraum },
    ...GEOZONEN.map(g => ({ key: `geo:${g.key}`, label: g.label, an: !!layerEcht.geozonen[g.key], unter: true, farbe: g.farbe })),
  ]
  const auswahlZuLayer = (a: DruckAuswahl): LayerSicht => {
    const e = a.ebenen
    const geo: Record<string, boolean> = {}
    for (const g of GEOZONEN) geo[g.key] = !!e[`geo:${g.key}`]
    return {
      ...layerEcht, grundkarte: a.grundkarte,
      einsatzort: !!e.einsatzort, symbole: !!e.symbole, rufnamen: !!e.rufnamen, suchgebiete: !!e.suchgebiete,
      flugrouten: !!e.flugrouten, pilotmarker: !!e.pilotmarker, drohnen: !!e.drohnen,
      abdeckung: !!e.abdeckung, fotos: !!e.fotos, hotspots: !!e.hotspots, luftraum: !!e.luftraum, geozonen: geo,
    }
  }
  const druckKopf = { titel: einsatz?.titel ?? 'Einsatz', nummer: (gewaehlt?.raw as any)?.einsatznummer as string | undefined, ort: einsatz?.einsatzort_adresse }
  const druckKnopf = (kompakt: boolean) => (
    <KartenDruck kompakt={kompakt} mapRef={mapRef} grundkarte={layerEcht.grundkarte} ebenen={druckEbenen}
      anwenden={a => setDruckLayer(a ? auswahlZuLayer(a) : null)}
      kopf={druckKopf}
      nachlaufMs={a => (a.ebenen.abdeckung || a.ebenen.fotos || a.ebenen.hotspots ? 2500 : 0)}
      legende={a => {
        const l = auswahlZuLayer(a)
        return legendeEintraege({
          zeichen: l.symbole ? symbole.map(effektiv) : [], einsatzort: l.einsatzort && !!einsatzOrt, suchgebiete: l.suchgebiete && suchgebiete.length > 0,
          geozonen: l.luftraum ? GEOZONEN.filter(g => l.geozonen[g.key]).map(g => g.key) : [],
        })
      }}
      attribution={a => quellenText(a.grundkarte, a.ebenen.luftraum && GEOZONEN.some(g => a.ebenen[`geo:${g.key}`]))} />
  )

  const panels: KartenPanelDef[] = [
    {
      id: 'ebenen', titel: 'Ebenen', icon: <Layers className="h-4 w-4" />, seite: 'links',
      inhalt: (
        <>
          {grundkarteWahl}
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {layerSchalter.map(l => (
                      <EbenenChip key={l.key} an={layer[l.key] !== false} onChange={v => setLayer(s => ({ ...s, [l.key]: v }))}>{l.label}</EbenenChip>
                    ))}
                    {/* Abdeckungsnachweis: Anteil der Suchgebiete, den die Kamera gesehen hat */}
                    <EbenenChip an={layer.abdeckung === true} onChange={v => setLayer(s => ({ ...s, abdeckung: v }))} title="Abdeckung (von der Kamera gesehen)">Abdeckung</EbenenChip>
                    {/* Foto-Lagebild und Hotspots (Fotos aus DJI Pilot 2) */}
                    <EbenenChip an={layer.fotos === true} onChange={v => setLayer(s => ({ ...s, fotos: v }))}>Lagebild (Fotos)</EbenenChip>
                    <EbenenChip an={layer.hotspots === true} onChange={v => setLayer(s => ({ ...s, hotspots: v }))}>Hotspots (IR)</EbenenChip>
                    {/* DIPUL-Geozonen nach §21h LuftVO */}
                    <EbenenChip an={layer.luftraum} title="Luftraum / Schutzgebiete (DIPUL · §21h LuftVO)" onChange={an => {
                      setLayer(v => ({
                        ...v, luftraum: an,
                        geozonen: an && Object.values(v.geozonen).every(x => !x)
                          ? { flugbeschraenkung: true, temporaer: true, kontrollzone: true }
                          : v.geozonen,
                      }))
                    }}>Luftraum / Schutzgebiete</EbenenChip>
                  </div>
                  {layer.abdeckung === true && (
                    <div className="px-1 pb-1">
                      <label className="flex items-center gap-2 text-xs text-dark-text-subtle">
                        zählt bis Höhe über Grund
                        <input type="number" min={5} max={500} step={5} value={layer.abdeckungHoehe ?? ABDECKUNG_STANDARD_HOEHE_M}
                          onChange={e => { const h = Number(e.target.value); if (h >= 5 && h <= 500) setLayer(v => ({ ...v, abdeckungHoehe: h })) }}
                          className="h-9 w-20 rounded-lg border border-dark-border bg-dark-bg-tertiary px-2 text-sm text-dark-text" />
                        m
                      </label>
                      <AbdeckungHinweis daten={abdeckung} />
                    </div>
                  )}
                  {layer.fotos === true && (
                    <div className="flex gap-1 px-1 pb-1">
                      {([['alle', 'Alle'], ['weit', 'Weitwinkel'], ['ir', 'IR'], ['pano', 'Pano']] as const).map(([k, tx]) => (
                        <button key={k} type="button" aria-pressed={fotoFilter === k} onClick={() => setFotoFilter(k)}
                          className={`min-h-[44px] flex-1 rounded-lg border px-2 text-xs font-semibold ${fotoFilter === k ? 'border-primary-500 bg-primary-500/20 text-dark-text' : 'border-dark-border text-dark-text-subtle'}`}>
                          {tx}
                        </button>
                      ))}
                    </div>
                  )}
                  {(layer.fotos === true || layer.hotspots === true) && <MedienStatusHinweis status={medienStatus} />}
                  {layer.luftraum && (
                    <div className="space-y-1.5 border-t border-white/10 pt-2">
                      <div className="flex flex-wrap gap-1.5">
                        {GEOZONEN.map(gz => (
                          <EbenenChip key={gz.key} an={!!layer.geozonen[gz.key]} farbe={gz.farbe}
                            onChange={v => setLayer({ ...layer, geozonen: { ...layer.geozonen, [gz.key]: v } })}>{gz.label}</EbenenChip>
                        ))}
                      </div>
                      <p className="text-xs leading-tight text-dark-text-subtle">
                        Daten: © DFS/DIPUL – Geozonen nach §21h LuftVO (kostenfrei).
                        Klick auf die Karte zeigt Details im Umfeld.
                      </p>
                    </div>
                  )}
                </div>
        </>
      ),
    },
    {
      id: 'zeichen', titel: 'Werkzeuge & Zeichen', icon: <Plus className="h-4 w-4" />, seite: 'links',
      inhalt: ({ einklappen }) => (
        <>
          <div className="mb-3 space-y-2">
            <Button variant={modus === 'suchgebiet' ? 'primary' : 'secondary'}
              className="w-full justify-start min-h-[44px]"
              onClick={() => { suchgebietUmschalten(); if (modus !== 'suchgebiet') einklappen() }}>
              <Plus className="h-4 w-4 mr-2" /> Suchgebiet zeichnen
            </Button>
            {modus && (
              <div className="rounded-lg border border-primary-500/30 bg-primary-500/10 px-3 py-2 text-xs text-primary-200">
                {modus === 'suchgebiet'
                  ? `Auf die Karte klicken, um Eckpunkte zu setzen.${(() => { const g = suchgebiete[suchgebiete.length - 1]; return g && g.punkte.length >= 3 ? ` Fläche: ${geodaesie.textFlaecheStufen(geodaesie.flaecheM2(g.punkte))}` : '' })()}`
                  : `„${symbolLabel(modus)}" ausgewählt — auf die Karte klicken zum Setzen.`}
                <button className="ml-2 min-h-[44px] underline" onClick={() => modus === 'suchgebiet' ? suchgebietUmschalten() : setModus(null)}>{modus === 'suchgebiet' ? 'fertig' : 'abbrechen'}</button>
              </div>
            )}
          </div>
                  <p className="mb-3 text-xs text-dark-text-subtle">
                    Symbol auf die Karte ziehen (Drag&nbsp;&&nbsp;Drop) — oder antippen und dann auf die Karte klicken.
                  </p>
                  <div className="space-y-3">
                    {[...new Set(palette.map(p => p.group))].map(gruppe => (
                      <div key={gruppe}>
                        <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-dark-text-subtle">{gruppe}</div>
                        <div className="flex flex-wrap gap-2">
                          {palette.filter(p => p.group === gruppe).map((p, i) => {
                            const btnClass = `relative flex h-[52px] min-w-[52px] items-center justify-center rounded-xl border bg-dark-bg-tertiary/60 px-2 transition-colors
                                ${modus === p.typ && (p.fahrzeugId ?? null) === modusFahrzeug && (p.truppKey ?? null) === modusTrupp
                                  ? 'border-primary-500 ring-2 ring-primary-500/40'
                                  : 'border-dark-border hover:border-primary-500/60'}`;
                            return (
                              <button
                                key={`${p.typ}-${i}`}
                                title={`${p.label}${p.truppKey && symbole.some(z => z.truppKey === p.truppKey) ? ' (bereits gesetzt)' : ''} — ziehen oder antippen`}
                                style={{ touchAction: 'none' }}
                                onPointerDown={e => {
                                  if (e.button !== 0) return
                                  downRef.current = { typ: p.typ, kennung: p.kennung, bezeichnung: p.bezeichnung, rufname: p.rufname, fahrzeugId: p.fahrzeugId, truppKey: p.truppKey, x: e.clientX, y: e.clientY, moved: false }
                                }}
                                onClick={() => {
                                  const gewaehlt = modus === p.typ && (p.fahrzeugId ?? null) === modusFahrzeug && (p.truppKey ?? null) === modusTrupp
                                  setModus(gewaehlt ? null : p.typ)
                                  setModusFahrzeug(gewaehlt ? null : p.fahrzeugId ?? null)
                                  setModusTrupp(gewaehlt ? null : p.truppKey ?? null)
                                  if (!gewaehlt) einklappen()
                                }}
                                className={btnClass}
                              >
                                <span dangerouslySetInnerHTML={{ __html: symbolSvg(p.typ, p.kennung) }} />
                                {p.truppKey && symbole.some(z => z.truppKey === p.truppKey) && (
                                  <span title="Bereits auf der Karte gesetzt" aria-label="Bereits gesetzt"
                                    className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-white ring-2 ring-dark-bg-secondary">
                                    <Check className="h-3 w-3" />
                                  </span>
                                )}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
        </>
      ),
    },
    {
      id: 'messen', titel: 'Messen', icon: <Ruler className="h-4 w-4" />, seite: 'rechts', badge: messen.art ? 'aktiv' : undefined,
      inhalt: ({ einklappen }) => <MessPanelInhalt messen={messen} einklappen={einklappen} />,
    },
    {
      id: 'drohnen', titel: 'Drohnen live', icon: <Crosshair className="h-4 w-4" />, seite: 'rechts', haltOffen: liveDrohnen.length > 0, badge: liveDrohnen.length || undefined,
      inhalt: (
        <DrohnenPanel drohnen={liveDrohnen} amBoden={liveAmBoden} onGeaendert={liveNeuLaden}
          onZentrieren={(lat, lon) => mapRef.current?.flyTo([lat, lon], Math.max(mapRef.current.getZoom(), 16))} />
      ),
    },
    {
      id: 'gebiete', titel: 'Gebiete & Übersicht', icon: <MapIcon className="h-4 w-4" />, seite: 'rechts',
      badge: symbole.length + suchgebiete.length || undefined,
      inhalt: (
        <div className="text-xs">
          {!symbole.length && !suchgebiete.length && <p className="text-dark-text-subtle">Noch keine Zeichen oder Suchgebiete gesetzt.</p>}
                {suchgebiete.length > 0 && (
                  <div className="mb-2 space-y-1">
                    {suchgebiete.map(sg => (
                      <div key={sg.id} className="flex flex-wrap items-center gap-2">
                        <Crosshair className="h-3.5 w-3.5 text-amber-400" />
                        <Input value={sg.kennung} className="h-9 max-w-[220px]"
                          onChange={e => setSuchgebiete(p => p.map(x => x.id === sg.id ? { ...x, kennung: e.target.value } : x))} />
                        <span className="text-dark-text-subtle">{sg.punkte.length} Eckpunkte</span>
                        {sg.punkte.length >= 3 && <span className="font-semibold text-amber-300" data-testid="suchgebiet-flaeche">{geodaesie.textFlaecheStufen(geodaesie.flaecheM2(sg.punkte))}</span>}
                        {abdeckungProzent(abdeckung, sg.id) !== null && (
                          <span className="font-semibold text-green-400" title="Anteil des Suchgebiets, den die Kamera gesehen hat">
                            {abdeckungProzent(abdeckung, sg.id)} % abgedeckt
                          </span>
                        )}
                        <button className="flex h-9 w-9 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11 items-center justify-center rounded-lg text-red-400 hover:bg-red-400/10"
                          onClick={() => loescheGebiet(sg.id)} title="Suchgebiet löschen">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-3 text-dark-text-subtle">
                  <span>{symbole.length} Symbol(e)</span>
                  <span>·</span>
                  <span>{suchgebiete.reduce((n, s) => n + s.punkte.length, 0)} Suchgebiet-Eckpunkte</span>
                  <span>·</span>
                  <span>Zeichen verschieben: ziehen · Infos und Bearbeiten: anklicken bzw. antippen</span>
                </div>
        </div>
      ),
    },
    {
      id: 'geozonen', titel: 'Geozonen im Umfeld', icon: <MapIcon className="h-4 w-4" />, seite: 'rechts', verbergen: !geozonOrt, oeffnenBei: geozonOrt,
      inhalt: geozonOrt && (
        <div>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="flex items-center gap-2 text-xs font-semibold text-dark-text">
                      <MapIcon className="h-3.5 w-3.5 text-primary-400" /> Geozonen im Umfeld
                    </span>
                    <button onClick={() => { setGeozonOrt(null); setGeozonTreffer(null) }} aria-label="Schließen"
                      className="flex h-11 w-11 items-center justify-center rounded-lg text-dark-text-muted hover:bg-dark-bg-tertiary">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mb-2 text-xs text-dark-text-subtle">
                    ca. ±150 m um {geozonOrt[0].toFixed(5)}, {geozonOrt[1].toFixed(5)}
                  </p>
                  {geozonLaedt ? (
                    <p className="text-xs text-dark-text-muted">Frage DIPUL ab…</p>
                  ) : !layer.luftraum ? (
                    <p className="text-xs text-dark-text-subtle">
                      Layer „Luftraum / Schutzgebiete" ist ausgeschaltet.
                    </p>
                  ) : geozonFehler && (geozonTreffer?.length ?? 0) === 0 ? (
                    <p className="text-xs text-red-300" role="alert">Geozonen konnten nicht abgefragt werden (DIPUL nicht erreichbar).</p>
                  ) : (geozonTreffer?.length ?? 0) === 0 ? (
                    <p className="text-xs text-dark-text-subtle">Keine Geozone im Umfeld.</p>
                  ) : (
                    <div className="space-y-2">
                      {geozonTreffer!.map((t, i) => (
                        <div key={i} className="rounded-lg border border-dark-border bg-dark-bg-tertiary/60 p-2">
                          <div className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: t.farbe }} />
                            <span className="text-xs text-dark-text-subtle">{t.zone}</span>
                          </div>
                          <div className="mt-0.5 text-sm font-semibold text-dark-text">{t.name}</div>
                          <dl className="mt-1 space-y-0.5 text-xs text-dark-text-muted">
                            <div className="flex gap-1"><span className="text-dark-text-subtle">Kategorie:</span> <span>{t.kategorie}</span></div>
                            <div className="flex gap-1"><span className="text-dark-text-subtle">Rechtsgrundlage:</span> <span>{t.rechtsgrundlage}</span></div>
                            {(t.untergrenze || t.obergrenze) && (
                              <div className="flex gap-1">
                                <span className="text-dark-text-subtle">Höhe:</span>
                                <span>{t.untergrenze || '—'} bis {t.obergrenze || '—'}</span>
                              </div>
                            )}
                            {t.gueltigkeit && (
                              <div className="flex gap-1"><span className="text-dark-text-subtle">Gültig:</span> <span>{t.gueltigkeit}</span></div>
                            )}
                          </dl>
                        </div>
                      ))}
                    </div>
                  )}
        </div>
      ),
    },
  ]

  return (
    <div className="-m-2 flex ui-fill min-h-[420px] flex-col gap-2 sm:-m-3">
      {bestaetigungsDialog}
      {positionOffen && <PositionZoomDialog onClose={() => setPositionOffen(false)}
        onZoom={(lat, lon) => { mapRef.current?.flyTo([lat, lon], Math.max(mapRef.current.getZoom(), 17)); setPositionOffen(false) }} />}

      {fehler && (
        <div className="shrink-0 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-200">{fehler}</div>
      )}

      {/* Einsatz wählt die Kopfzeile (global); Hinweise nur bei Bedarf */}
      {(ladeFehler || datenFehler.length > 0 || kraefteFehler) && (
        <div className="shrink-0 space-y-1 rounded-xl border border-dark-border bg-dark-bg-secondary/60 px-3 py-1.5">
          {ladeFehler && <p className="text-xs text-red-300" role="alert">{ladeFehler} <button type="button" className="underline" onClick={() => setLadeNeu(n => n + 1)}>Erneut versuchen</button></p>}
          {datenFehler.length > 0 && <p className="text-xs text-amber-300" role="alert">Nicht aktualisiert: {datenFehler.join(', ')} – letzte bekannte Daten werden angezeigt.</p>}
          {kraefteFehler && <p className="text-xs text-amber-300" role="alert">Besatzung konnte nicht aktualisiert werden – letzte bekannte Zuteilung wird angezeigt.</p>}
        </div>
      )}

      {!einsatzId ? (
        <div className="min-h-0 flex-1 overflow-y-auto"><RequireEinsatz hinweis="Die taktische Karte ist einsatzgebunden. Bitte einen Einsatz wählen.">{() => null}</RequireEinsatz></div>
      ) : (
        <div className="min-h-0 flex-1">
          <KartenBuehne seitenKey="fuehrung-taktische-karte" mapRef={mapRef} panels={panels} kopfAktionen={<>
            {einsatzId && <FreigabeLinkButton einsatzId={einsatzId} kompakt />}
            {druckKnopf(true)}
            <FensterKnopf ansicht="karte" kompakt />
          </>}
          handyAktionen={einsatzId ? <>{druckKnopf(true)}<FreigabeLinkButton einsatzId={einsatzId} kompakt /></> : undefined}
          kopfStatus={speicherStatus === 'fehler'
            ? <span role="status" className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/40 bg-dark-bg-secondary/95 px-2 py-1 text-xs font-semibold text-red-300 shadow-2xl"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />Speichern fehlgeschlagen</span>
            : (speicherStatus === 'speichert' || speicherStatus === 'gespeichert')
              ? <span role="status" className="rounded-md bg-dark-bg-secondary/80 px-1.5 py-0.5 text-[11px] text-dark-text-muted">{speicherStatus === 'speichert' ? 'Speichert…' : 'Gespeichert'}</span>
              : undefined}>
              <MapContainer center={mitte} zoom={einsatzOrt ? 14 : 12} maxZoom={20} className="h-full w-full" zoomControl={false}>
                <ZoomControl position="bottomright" />
                <KartenRef mapRef={mapRef} />
                <ZentriereAuf ziel={einsatzOrt} zoom={einsatzOrt ? 15 : 12} />
                <KlickHandler aktiv={true} onKlick={klickAufKarte} />
                <MessLayer messen={messen} />

                {(() => {
                  const gk = layer.grundkarte === 'keine' ? undefined : (GRUNDKARTEN.find(g => g.key === layer.grundkarte) ?? GRUNDKARTEN[0])
                  if (!gk) return null   // „keine“ gibt es nur beim Drucken
                  // key erzwingt einen neuen TileLayer – react-leaflet tauscht die url sonst ohne Attribution
                  return <TileLayer key={gk.key} url={gk.url} attribution={gk.attribution}
                    maxNativeZoom={gk.maxNativeZoom} maxZoom={20} />
                })()}

                {/* DIPUL-Geozonen (§21h LuftVO) als WMS-Overlays */}
                {layer.luftraum && GEOZONEN.filter(gz => layer.geozonen[gz.key]).map(gz => (
                  <WmsGeozon key={gz.key} layer={gz.layer} opacity={0.75} />
                ))}

                {/* Rote Markierung am Einsatzort */}
                {layer.einsatzort && einsatzOrt && (
                  <Marker position={einsatzOrt} icon={divIcon({
                    html: `<div style="width:18px;height:18px;border-radius:9999px;background:#dc2626;border:3px solid #fff;box-shadow:0 0 0 3px rgba(220,38,38,.45)"></div>`,
                    className: '', iconSize: [18, 18], iconAnchor: [9, 9],
                  })}>
                    <Tooltip direction="top" offset={[0, -12]}>
                      {einsatz?.titel} — Einsatzort
                    </Tooltip>
                  </Marker>
                )}

                {/* Suchgebiete */}
                {layer.suchgebiete && suchgebiete.map(sg => (
                  <Polygon key={sg.id} positions={sg.punkte}
                    pathOptions={{ color: '#f59e0b', weight: 2, fillOpacity: 0.12, dashArray: '6 4' }}>
                    {sg.punkte.length >= 3 && (
                      <Tooltip sticky>{sg.kennung} · {geodaesie.textFlaecheStufen(geodaesie.flaecheM2(sg.punkte))}
                        {layer.abdeckung === true && abdeckungProzent(abdeckung, sg.id) !== null && <> · {abdeckungProzent(abdeckung, sg.id)} % abgedeckt</>}
                      </Tooltip>
                    )}
                  </Polygon>
                ))}
                {layer.abdeckung === true && <AbdeckungLayer daten={abdeckung} />}
                {layer.fotos === true && <FotoLayer fotos={fotos} filter={fotoFilter} />}
                {layer.hotspots === true && <HotspotLayer hotspots={hotspots} />}

                {/* Eckpunkte: ziehen = verschieben, Klick = löschen */}
                {layer.suchgebiete && suchgebiete.flatMap(sg => sg.punkte.map((pt, i) => (
                  <Marker key={`${sg.id}-${i}`} position={pt} draggable
                    eventHandlers={{
                      dragend: e => {
                        letztesZiehen.current = Date.now()
                        const p = (e.target as L.Marker).getLatLng()
                        verschiebeEckpunkt(sg.id, i, p.lat, p.lng)
                      },
                      click: () => {
                        // Sicherheitsnetz: unmittelbar nach dem Verschieben nicht löschen
                        if (Date.now() - letztesZiehen.current < 350) return
                        loescheEckpunkt(sg.id, i)
                      },
                    }}
                    icon={divIcon({
                      html: `<div style="display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:9999px;background:#f59e0b;color:#111827;font:700 12px Arial;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.45);cursor:pointer">${i + 1}</div>`,
                      className: '', iconSize: [26, 26], iconAnchor: [13, 13],
                    })}>
                    <Tooltip direction="top">
                      {sg.kennung} · Punkt {i + 1}<br />ziehen = verschieben · Klick = löschen
                    </Tooltip>
                  </Marker>
                )))}

                {/* Taktische Symbole */}
                {layer.symbole && symbole.map(s => (
                  <StabilerMarker key={s.id} lat={s.lat} lng={s.lng} draggable icon={icon(effektiv(s), bearbeitet === s.id)}
                    eventHandlers={{
                      dragend: e => { const p = (e.target as L.Marker).getLatLng(); verschiebeSymbol(s.id, p.lat, p.lng) },
                      click: () => setBearbeitet(b => (b === s.id ? null : s.id)),
                    }}>
                    {hatMaus && bearbeitet !== s.id && (
                      <Tooltip direction="top" offset={[0, -14]}>
                        <SymbolTooltip s={effektiv(s)} besatzung={besatzungFuer(s)} trupp={truppAnzeige(s, trupps, nutzerNamen, truppRufnamen)} />
                      </Tooltip>
                    )}
                  </StabilerMarker>
                ))}
                {layer.flugrouten !== false && <FlugroutenLayer routen={flugrouten} drohnen={liveDrohnen} />}
                {layer.pilotmarker !== false && <PilotMarkierungenLayer markierungen={pilotMarkierungen} />}
                {layer.drohnen !== false && <DrohnenLayer drohnen={liveDrohnen} />}
              </MapContainer>

              {bearbeitet && (() => {
                const s = symbole.find(x => x.id === bearbeitet)
                if (!s) return null
                return (
                  <SymbolInfoKarte key={s.id} positionCls="left-1/2 top-3 z-[34] -translate-x-1/2" s={effektiv(s)} besatzung={besatzungFuer(s)} trupp={truppAnzeige(s, trupps, nutzerNamen, truppRufnamen)} nurLesen={false}
                    onAendern={patch => aendereSymbol(s.id, patch)}
                    onLoeschen={() => loescheSymbol(s.id)}
                    onSchliessen={() => setBearbeitet(null)} />
                )
              })()}
          </KartenBuehne>
        </div>
      )}

      {/* Drag-Ghost */}
      {dragSymbol && (
        <div className="pointer-events-none fixed z-[9999] opacity-90"
          style={{ left: dragSymbol.x - 26, top: dragSymbol.y - 20 }}>
          <span dangerouslySetInnerHTML={{ __html: symbolSvg(dragSymbol.typ, dragSymbol.kennung) }} />
        </div>
      )}
    </div>
  )
}
